import { CircleCheck } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useDownloadTemplate from '../hooks/useDownloadTemplate';
import type { ExportPerformanceResultsFilters } from '../hooks/useExportData';
import useExportData from '../hooks/useExportData';
import useImportPerformance from '../hooks/useImportPerformance';
import useValidateImport from '../hooks/useValidateImport';
import { BulkActionEnum, BulkStepEnum, useBulkModificationState } from '../useBulkModificationState';
import { getTemplateKeyByResultType } from '../utils';
import ChooseMethodStep from './ChooseMethodStep';
import ImportValidationStep from './ImportValidationStep';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import PrimaryButton from '@/components/common/PrimaryButton';
import { PerformanceResultType } from '@/gql/enums';
import { success as showSuccessNotification } from '@/utils/notifications/actions';

interface BulkModificationModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    resultType: PerformanceResultType;
    exportFilters?: ExportPerformanceResultsFilters;
}

const BulkModificationModal = ({ open, onClose, resultType, exportFilters }: BulkModificationModalProps) => {
    const { t } = useTranslation('performanceResult');
    const { state, actions, selectors } = useBulkModificationState(resultType);

    const key = getTemplateKeyByResultType(resultType);

    const performanceName = useMemo(() => {
        switch (resultType) {
            case PerformanceResultType.Ippt:
                return t('tabs.ippt');
            case PerformanceResultType.Soc:
                return t('tabs.soc');
            case PerformanceResultType.Bmp:
                return t('tabs.bmp');
            case PerformanceResultType.FinSwim:
                return t('tabs.finSwim');
            case PerformanceResultType.March20km:
                return t('tabs.20kmCombatMarch');
            case PerformanceResultType.Weight:
                return t('tabs.weight');
            default:
                return null;
        }
    }, [resultType, t]);

    const notificationMessage = useCallback((title: string, description: string) => {
        return (
            <div className="grid rounded-[10px] bg-teal-50 p-5">
                <div className="flex items-center gap-2 text-base font-bold text-neutral-900">
                    <CircleCheck className="size-4" color="var(--color-teal-600)" />
                    {title}
                </div>
                <div className="text-base font-medium text-neutral-500">{description}</div>
            </div>
        );
    }, []);

    const { downloadTemplate } = useDownloadTemplate();
    const handleDownload = useCallback(async () => {
        actions.setIsProcessingStart();
        try {
            await downloadTemplate(key);
            actions.setNotificationMessage(
                notificationMessage(
                    t('bulk.banners.downloadCompleteTitle'),
                    t('bulk.banners.downloadCompleteDescription')
                )
            );
        } finally {
            actions.setIsProcessingEnd();
        }
    }, [actions, downloadTemplate, key, notificationMessage, t]);

    const { exportData } = useExportData();
    const handleExportData = useCallback(async () => {
        actions.setIsProcessingStart();
        try {
            await exportData(resultType, exportFilters);
            actions.setNotificationMessage(
                notificationMessage(
                    t('bulk.banners.exportCompleteTitle'),
                    t('bulk.banners.exportCompleteDescription', { performance: performanceName })
                )
            );
        } finally {
            actions.setIsProcessingEnd();
        }
    }, [actions, exportData, resultType, exportFilters, notificationMessage, t, performanceName]);

    const { validate } = useValidateImport();
    const { importAndPoll } = useImportPerformance();

    const title = useMemo(() => {
        switch (state.step) {
            case BulkStepEnum.ImportSelect:
            case BulkStepEnum.ChooseAction:
                switch (resultType) {
                    case PerformanceResultType.Ippt:
                        return t('bulk.titles.chooseIppt');
                    case PerformanceResultType.Soc:
                        return t('bulk.titles.chooseSoc');
                    case PerformanceResultType.Bmp:
                        return t('bulk.titles.chooseBmp');
                    case PerformanceResultType.FinSwim:
                        return t('bulk.titles.chooseFinSwim');
                    case PerformanceResultType.March20km:
                        return t('bulk.titles.chooseMarch20km');
                    case PerformanceResultType.Weight:
                        return t('bulk.titles.chooseWeight');
                    default:
                        return t('bulk.titles.choose');
                }
            case BulkStepEnum.Validate:
                return t('bulk.titles.import');
            default:
                return t('bulk.titles.choose');
        }
    }, [state, t, resultType]);

    const content = useMemo(() => {
        // Note: We reuse ChooseMethodStep for both choose and import-select for now (shows upload UI when Import selected)
        if (state.step === BulkStepEnum.ChooseAction || state.step === BulkStepEnum.ImportSelect) {
            return (
                <div>
                    <ChooseMethodStep
                        isProcessing={state.isProcessing}
                        action={state.action}
                        onActionChange={actions.chooseAction}
                        onFileChange={file => actions.setFile(file)}
                        onDownloadTemplate={handleDownload}
                        notificationMessage={state.notificationMessage}
                    />
                </div>
            );
        }

        // Import validation view
        if (state.step === BulkStepEnum.Validate) {
            const validation = state.validation;
            if (!state.file || !validation) {
                return null;
            }
            return (
                <ImportValidationStep
                    file={state.file}
                    readyCount={validation.readyCount || 0}
                    errorCount={validation.errorCount || 0}
                    errors={validation.errors || []}
                />
            );
        }
        return null;
    }, [state, actions, handleDownload]);

    const footer = useMemo(() => {
        const onCancel = () => {
            actions.reset();
            onClose(false);
        };

        const onPrimary = () => {
            if (state.step === BulkStepEnum.ChooseAction) {
                if (state.action === BulkActionEnum.Export) {
                    handleExportData();
                    return;
                }
                actions.next();
                return;
            }

            if (state.step === BulkStepEnum.ImportSelect) {
                // Trigger validation
                if (!state.file) {
                    return;
                }
                actions.submitStart();
                void (async () => {
                    const summary = await validate(resultType, state.file!);
                    if (summary) {
                        actions.setValidation(summary);
                        actions.setStep(BulkStepEnum.Validate);
                    }
                    actions.submitEnd();
                })();
                return;
            }

            if (state.step === BulkStepEnum.Validate) {
                // Import and poll until completion
                if (!state.file) {
                    return;
                }
                actions.submitStart();
                void (async () => {
                    const status = await importAndPoll(resultType, state.file!);

                    if (status === 'success') {
                        showSuccessNotification({
                            message: t('bulk.banners.importComplete'),
                            description: t('bulk.banners.importCompleteDescription'),
                        });
                        actions.submitEnd();
                        onClose(true);
                        return;
                    }
                    actions.submitEnd();
                })();
                return;
            }
        };

        const primaryLabel = (() => {
            if (state.step === BulkStepEnum.ChooseAction) {
                if (state.action === BulkActionEnum.Export) {
                    return t('bulk.buttons.download');
                }
                return t('bulk.buttons.next');
            }
            if (state.step === BulkStepEnum.ImportSelect) {
                return state.isSubmitting ? t('bulk.buttons.validating') : t('bulk.buttons.next');
            }
            if (state.step === BulkStepEnum.Validate) {
                return state.isSubmitting ? t('bulk.buttons.importing') : t('bulk.buttons.import');
            }
            return t('bulk.buttons.next');
        })();

        const primaryDisabled = (() => {
            if (state.isSubmitting || state.isProcessing) {
                return true;
            }
            if (state.step === BulkStepEnum.ChooseAction) {
                if (state.action === BulkActionEnum.Export) {
                    return false;
                }
                if (state.action === BulkActionEnum.Import) {
                    return !selectors.hasFile;
                }
                return true; // no action selected
            }
            if (state.step === BulkStepEnum.ImportSelect) {
                return !selectors.hasFile;
            }
            if (state.step === BulkStepEnum.Validate) {
                return !state.validation?.readyCount || state.validation.readyCount === 0;
            }
            return false;
        })();

        return (
            <div className="mt-5 flex w-full justify-end">
                <div className="flex space-x-2">
                    <Button variant="secondary" onClick={onCancel}>
                        {t('bulk.buttons.cancel')}
                    </Button>
                    <Button variant="primary" onClick={onPrimary} disabled={primaryDisabled}>
                        {primaryLabel}
                    </Button>
                </div>
            </div>
        );
    }, [actions, onClose, selectors.hasFile, t, handleExportData, state, importAndPoll, resultType, validate]);

    const fileBrowseButton = useMemo(() => {
        if (state.step === BulkStepEnum.Validate) {
            return (
                <PrimaryButton
                    className="h-auto bg-transparent p-0 text-base font-semibold text-blue-600 shadow-none hover:cursor-pointer hover:underline"
                    onClick={() => actions.reset()}
                >
                    {t('bulk.buttons.uploadDifferentFile')}
                </PrimaryButton>
            );
        }
        return null;
    }, [state.step, t, actions]);

    return (
        <Modal
            open={open}
            onClose={() => onClose(false)}
            title={title}
            largeTitle={true}
            titleRight={fileBrowseButton}
            showCloseButton={false}
            className={`w-full ${state.step === BulkStepEnum.Validate ? '!w-[800px] !max-w-[800px]' : '!w-full !max-w-[512px]'}`}
            footer={footer}
        >
            {content}
        </Modal>
    );
};

export default BulkModificationModal;
